import { StandardMaterial, Texture, type Scene } from '@babylonjs/core';
import { createLogger } from '@sorrem/shared';
import { createBrickTexture, createRockTexture } from './procedural-textures';

const logger = createLogger('engine:materials-loader');

export interface MaterialConfig {
    id: string;
    name: string;
    type: string;
    texture: string;
    normalMap?: string;
    /** Grayscale specular intensity map; replaces the flat specular color. */
    specularMap?: string;
    /**
     * World meters one repeat of the textures covers, on meshes with
     * world-space UVs (the floor). Defaults to 1.
     */
    textureSpan?: number;
    roughness?: number;
    metallic?: number;
}

/** One scene's materials: those made, and those still loading. */
interface SceneMaterials {
    readonly materials: Map<string, StandardMaterial>;
    readonly loads: Map<string, Promise<StandardMaterial | null>>;
}

/**
 * Loads and caches 3D materials for walls and floors.
 * Materials are loaded individually from /assets/materials/:id using content negotiation.
 * Each scene has its own (#324): a material belongs to the scene it was made
 * in, so a later scene never gets a disposed one's, and one disposed by
 * mistake is made again.
 */
export class MaterialsLoader {
    private readonly sceneMaterials = new Map<Scene, SceneMaterials>();
    private basePath: string;

    /**
     * @param basePath Base URL path to materials assets endpoint (default: /assets/materials)
     */
    constructor(basePath: string = '/assets/materials') {
        this.basePath = basePath;
    }

    /**
     * Get or create a material by ID, fetching it from the content-negotiated API.
     *
     * Callers that request the same materialId concurrently (e.g. building
     * every tile type in a catalog in parallel) share one in-flight fetch
     * instead of each racing to check the empty cache and firing its own
     * duplicate request/material creation.
     */
    async loadMaterial(materialId: string, scene: Scene): Promise<StandardMaterial | null> {
        const { materials, loads } = this.materialsOf(scene);
        const cached = materials.get(materialId);
        if (cached) {
            return cached;
        }

        const inFlight = loads.get(materialId);
        if (inFlight) {
            return inFlight;
        }

        const load = this.loadAndCreateMaterial(materialId, scene).finally(() => {
            loads.delete(materialId);
        });
        loads.set(materialId, load);
        return load;
    }

    /** The scene's materials, dropped when it is disposed (its materials go with it). */
    private materialsOf(scene: Scene): SceneMaterials {
        let entry = this.sceneMaterials.get(scene);
        if (!entry) {
            entry = { materials: new Map(), loads: new Map() };
            this.sceneMaterials.set(scene, entry);
            scene.onDisposeObservable.addOnce(() => this.sceneMaterials.delete(scene));
        }
        return entry;
    }

    private async loadAndCreateMaterial(materialId: string, scene: Scene): Promise<StandardMaterial | null> {
        try {
            const response = await fetch(`${this.basePath}/${encodeURIComponent(materialId)}`, {
                headers: { Accept: 'application/json' },
            });

            if (!response.ok) {
                logger.warn(`Material not found: ${materialId} (${response.status})`);
                return null;
            }

            const materialConfig = (await response.json()) as MaterialConfig;
            if (scene.isDisposed) {
                return null;
            }
            const material = this.createMaterial(materialConfig, scene);
            const { materials } = this.materialsOf(scene);
            materials.set(materialId, material);
            material.onDisposeObservable.addOnce(() => {
                if (materials.get(materialId) === material) materials.delete(materialId);
            });
            return material;
        } catch (error) {
            logger.warn(`Failed to load material ${materialId}:`, error);
            return null;
        }
    }

    /**
     * Create a material from config.
     */
    private createMaterial(config: MaterialConfig, scene: Scene): StandardMaterial {
        const material = new StandardMaterial(config.id, scene);
        const repeat = 1 / (config.textureSpan ?? 1);

        // A failed diffuse texture falls back to a procedural one where there is one.
        material.diffuseTexture = loadMaterialTexture(config.texture, scene, repeat, (failed, message) => {
            logger.warn(`Failed to load diffuse texture for ${config.id}:`, message);
            if (material.diffuseTexture !== failed) return;
            if (config.id === 'wall') {
                material.diffuseTexture = createBrickTexture(512, 512, scene);
            } else if (config.id === 'floor') {
                material.diffuseTexture = createRockTexture(512, 512, scene);
            } else {
                material.diffuseTexture = null;
            }
        });

        if (config.normalMap) {
            material.bumpTexture = loadMaterialTexture(config.normalMap, scene, repeat, (failed, message) => {
                logger.warn(`Failed to load normal map for ${config.id}:`, message);
                if (material.bumpTexture === failed) material.bumpTexture = null;
            });
        }

        if (config.specularMap) {
            material.specularTexture = loadMaterialTexture(config.specularMap, scene, repeat, (failed, message) => {
                logger.warn(`Failed to load specular map for ${config.id}:`, message);
                if (material.specularTexture === failed) material.specularTexture = null;
            });
        }

        // Set material properties
        material.specularColor.set(0.2, 0.2, 0.2);
        material.backFaceCulling = false;
        material.twoSidedLighting = true;

        // Set roughness (inverted for specularPower in StandardMaterial)
        if (config.roughness !== undefined) {
            const roughness = Math.max(0.1, Math.min(1, config.roughness));
            material.specularPower = 256 * (1 - roughness);
        }

        return material;
    }

    /**
     * Clear all cached materials.
     */
    clearCache(): void {
        this.sceneMaterials.forEach(({ materials }) => {
            // Each material's textures are its own.
            materials.forEach((material) => material.dispose(false, true));
            materials.clear();
        });
        this.sceneMaterials.clear();
    }

    /**
     * Dispose all cached materials.
     * Call this when unloading a scene to prevent texture/material leaks.
     */
    dispose(): void {
        this.clearCache();
    }
}

/**
 * A material texture from /assets/materials, repeated `repeat` times per
 * meter. Texture's load is async and reports failure via the onError
 * callback, not by throwing - a try/catch around `new Texture(...)` never
 * sees a 404 (the constructor returns immediately). The failed texture is
 * disposed, then `onFailed` takes it off the material.
 */
function loadMaterialTexture(
    file: string,
    scene: Scene,
    repeat: number,
    onFailed: (failed: Texture, message: string | undefined) => void,
): Texture {
    const texture = new Texture(`/assets/materials/${file}`, scene, undefined, undefined, undefined, undefined, (message) => {
        texture.dispose();
        onFailed(texture, message);
    });
    texture.uScale = repeat;
    texture.vScale = repeat;
    return texture;
}

/**
 * Global materials loader instance.
 */
let globalMaterialsLoader: MaterialsLoader | null = null;

export function getMaterialsLoader(basePath?: string): MaterialsLoader {
    if (!globalMaterialsLoader) {
        globalMaterialsLoader = new MaterialsLoader(basePath);
    }
    return globalMaterialsLoader;
}

/**
 * Dispose the global materials loader and clear all cached materials.
 * Call this when unloading a scene/game to prevent texture/material leaks.
 */
export function disposeMaterialsLoader(): void {
    if (globalMaterialsLoader) {
        globalMaterialsLoader.dispose();
        globalMaterialsLoader = null;
    }
}
